import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, HelpCircle, List, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import ReactMarkdown from 'react-markdown';
import { useLanguage } from '../context/LanguageContext';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const FAQPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [faqSections, setFaqSections] = useState([]);
  const [showIndex, setShowIndex] = useState(true);

  useEffect(() => {
    // Fetch FAQ content from public folder
    setLoading(true);
    fetch('/documentation/FAQs.md')
      .then(res => {
        if (!res.ok) throw new Error('Failed to load FAQs');
        return res.text();
      })
      .then(text => {
        setContent(text);
        parseFAQContent(text);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading FAQs:', err);
        setContent('# Error Loading FAQs\n\nPlease try again later.');
        setLoading(false);
      });
  }, []);

  const parseFAQContent = (text) => {
    // Split content into sections by ## headings (categories)
    const sections = [];
    const lines = text.split('\n');
    let currentSection = null;
    let currentQuestion = null;
    let currentAnswer = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Skip H1 titles
      if (line.match(/^#\s+/) && !line.match(/^##/)) {
        continue;
      }
      
      // H2 - Category heading
      if (line.match(/^##\s+/) && !line.match(/^###/)) {
        // Save previous question if exists
        if (currentQuestion && currentSection) {
          currentSection.questions.push({
            question: currentQuestion,
            answer: currentAnswer.join('\n').trim()
          });
          currentQuestion = null;
          currentAnswer = [];
        }
        
        // Start new section
        const title = line.replace(/^##\s+/, '');
        currentSection = {
          id: `section-${sections.length}`,
          title: title,
          questions: []
        };
        sections.push(currentSection);
      }
      // H3 - Question 
      else if (line.match(/^###\s+/)) {
        // Save previous question if exists
        if (currentQuestion && currentSection) {
          currentSection.questions.push({
            question: currentQuestion,
            answer: currentAnswer.join('\n').trim()
          });
          currentAnswer = [];
        }
        
        // Start new question
        currentQuestion = line.replace(/^###\s+/, '');
      }
      // Content after question
      else if (currentQuestion && line.trim() && !line.match(/^---/)) {
        currentAnswer.push(line);
      }
    }
    
    // Save last question
    if (currentQuestion && currentSection) {
      currentSection.questions.push({
        question: currentQuestion,
        answer: currentAnswer.join('\n').trim()
      });
    }

    setFaqSections(sections.filter(s => s.questions.length > 0));
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50">
      {/* Header */}
      <header className="bg-white border-b-2 border-cyan-500 shadow-md sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="flex items-center gap-2 border-cyan-500 text-cyan-600 hover:bg-cyan-50"
              >
                <ArrowLeft className="h-4 w-4" />
                {language === 'ar' ? 'العودة للرئيسية' : 'Back to Home'}
              </Button>
              <div className="flex items-center gap-2">
                <HelpCircle className="h-6 w-6 text-cyan-600" />
                <h1 className="text-2xl font-bold text-gray-800">
                  {language === 'ar' ? 'الأسئلة الشائعة' : 'Frequently Asked Questions'}
                </h1>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => setShowIndex(!showIndex)}
              className="flex items-center gap-2 border-cyan-500 text-cyan-600 hover:bg-cyan-50"
            >
              <List className="h-4 w-4" />
              {showIndex ? (language === 'ar' ? 'إخفاء الفهرس' : 'Hide Index') : (language === 'ar' ? 'إظهار الفهرس' : 'Show Index')}
            </Button>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="container mx-auto px-6 py-3">
        <nav className="text-sm text-gray-600">
          <span className="hover:text-cyan-600 cursor-pointer" onClick={() => navigate('/')}>
            {language === 'ar' ? 'الرئيسية' : 'Home'}
          </span>
          <span className="mx-2">/</span>
          <span className="text-cyan-600 font-medium">
            {language === 'ar' ? 'الأسئلة الشائعة' : 'FAQs'}
          </span>
        </nav>
      </div>

      {/* Content */}
      <main className="container mx-auto px-6 py-8 pb-16">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
          </div>
        ) : (
          <div className="flex gap-6">
            {/* Index Sidebar */}
            {showIndex && faqSections.length > 0 && (
              <aside className="w-80 flex-shrink-0">
                <div className="sticky top-24 bg-white rounded-xl shadow-lg p-6 max-h-[calc(100vh-150px)] overflow-y-auto">
                  <h2 className="text-lg font-bold text-cyan-700 mb-4 flex items-center gap-2">
                    <List className="h-5 w-5" />
                    {language === 'ar' ? 'الفهرس' : 'Table of Contents'}
                  </h2>
                  <nav className="space-y-2">
                    {faqSections.map((section, index) => (
                      <button
                        key={index}
                        onClick={() => scrollToSection(section.id)}
                        className="block w-full text-left py-2 px-3 rounded-lg transition-colors hover:bg-cyan-50 hover:text-cyan-700 font-medium text-cyan-600 text-sm"
                      >
                        {section.title}
                      </button>
                    ))}
                  </nav>
                </div>
              </aside>
            )}

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
                <h1 className="text-4xl font-bold text-cyan-700 mb-8 pb-3 border-b-2 border-cyan-200">
                  {language === 'ar' ? 'الأسئلة الشائعة' : 'Frequently Asked Questions'}
                </h1>

                {faqSections.map((section, sectionIndex) => (
                  <div key={sectionIndex} id={section.id} className="mb-8 scroll-mt-24">
                    <h2 className="text-2xl font-semibold text-cyan-600 mb-4">
                      {section.title}
                    </h2>
                    
                    <Accordion type="single" collapsible className="space-y-2">
                      {section.questions.map((item, qIndex) => (
                        <AccordionItem 
                          key={qIndex} 
                          value={`item-${sectionIndex}-${qIndex}`}
                          className="border border-gray-200 rounded-lg px-4 hover:border-cyan-300 transition-colors"
                        >
                          <AccordionTrigger className="text-left font-medium text-gray-800 hover:text-cyan-600 py-4 hover:no-underline">
                            <span className="flex items-start gap-2">
                              <ChevronDown className="h-5 w-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                              <span>{item.question}</span>
                            </span>
                          </AccordionTrigger>
                          <AccordionContent className="text-gray-700 pb-4 pt-2 pl-7">
                            <ReactMarkdown
                              components={{
                                p: ({node, ...props}) => <p className="mb-3 leading-relaxed" {...props} />,
                                ul: ({node, ...props}) => <ul className="list-disc list-inside space-y-2 mb-3" {...props} />,
                                ol: ({node, ...props}) => <ol className="list-decimal list-inside space-y-2 mb-3" {...props} />,
                                li: ({node, ...props}) => <li className="ml-4" {...props} />,
                                strong: ({node, ...props}) => <strong className="font-semibold text-gray-900" {...props} />,
                                a: ({node, ...props}) => <a className="text-cyan-600 hover:text-cyan-700 underline" {...props} />,
                              }}
                            >
                              {item.answer}
                            </ReactMarkdown>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Quick Links Footer */}
      <footer className="container mx-auto px-6 py-8 max-w-7xl">
        <div className="bg-cyan-50 rounded-lg p-6 border border-cyan-200">
          <h3 className="text-lg font-semibold text-cyan-800 mb-3">
            {language === 'ar' ? 'مصادر مفيدة أخرى' : 'Other Helpful Resources'}
          </h3>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/user-guide')}
              className="border-cyan-500 text-cyan-600 hover:bg-cyan-100"
            >
              {language === 'ar' ? 'دليل المستخدم' : 'User Guide'}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/privacy-policy')}
              className="border-cyan-500 text-cyan-600 hover:bg-cyan-100"
            >
              {language === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/terms')}
              className="border-cyan-500 text-cyan-600 hover:bg-cyan-100"
            >
              {language === 'ar' ? 'شروط الاستخدام' : 'Terms of Use'}
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FAQPage;
